import React, { useState, useEffect } from 'react';
import { addDays, format, startOfWeek, parseISO, addHours, isToday, isValid } from 'date-fns';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Room, Booking } from './BookingCalendar';
import { BookingDialog } from './BookingDialog';
import { EditBookingDialog } from './EditBookingDialog';
import { staffMembers } from './BookingCalendar';
import { useDevice } from '@/hooks/use-device';
import { ChevronRight, ChevronLeft } from 'lucide-react';

type WeeklyCalendarProps = {
  room: Room | null;
  bookings: Booking[];
  date: Date;
  onBookingCreate: (booking: Booking) => void;
  onBookingUpdate: (booking: Booking) => void;
  onBookingDelete: (bookingId: number) => void;
  onDayClick?: (date: Date) => void;
};

const statusColors = {
  paid: 'bg-blue-100 border-l-4 border-blue-500 text-blue-800 shadow hover:shadow-md hover:bg-blue-200/80 transition-all',
  booked: 'bg-emerald-100 border-l-4 border-emerald-500 text-emerald-800 shadow hover:shadow-md hover:bg-emerald-200/80 transition-all',
  pending: 'bg-amber-100 border-l-4 border-amber-500 text-amber-800 shadow hover:shadow-md hover:bg-amber-200/80 transition-all'
};

// Create hours array from 9:00 to 02:00 next day
const HOURS = [
  ...Array.from({ length: 15 }, (_, i) => i + 9), // 9:00 to 23:00
  0, 1, 2 // 00:00 to 02:00 next day
];

// Helper function to safely parse date strings
const safeParseISO = (dateStr: string, timeStr: string): Date | null => {
  try {
    // Check if the date string already contains a time component (T)
    const cleanDateStr = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const dateTimeStr = `${cleanDateStr}T${timeStr}`;
    
    const parsedDate = parseISO(dateTimeStr);
    if (!isValid(parsedDate)) {
      console.warn(`Invalid date after parsing: ${dateTimeStr}`);
      return null;
    }
    return parsedDate;
  } catch (error) {
    console.error(`Error parsing date: ${dateStr} with time ${timeStr}`, error);
    return null;
  }
};

// Helper function to safely format dates
const safeFormatDate = (date: Date | null, formatStr: string, fallback: string = 'Invalid date'): string => {
  if (!date || !isValid(date)) return fallback;
  try {
    return format(date, formatStr);
  } catch (error) {
    console.error(`Error formatting date: ${date}`, error);
    return fallback;
  }
};

export function WeeklyCalendar({
  room,
  bookings,
  date,
  onBookingCreate,
  onBookingUpdate,
  onBookingDelete,
  onDayClick
}: WeeklyCalendarProps) {
  const { isMobile, isTablet } = useDevice();
  const isCompactView = isMobile || isTablet;

  const [selectedSlot, setSelectedSlot] = useState<{
    date: Date;
    hour: number;
    isNextDay: boolean;
  } | null>(null);

  const [selectedBooking, setSelectedBooking] = useState<{
    booking: Booking;
    room: Room;
  } | null>(null);

  const [localBookings, setLocalBookings] = useState(bookings);
  const [currentDayIndex, setCurrentDayIndex] = useState(isCompactView ? 0 : -1);

  useEffect(() => {
    setLocalBookings(bookings);
  }, [bookings]);

  // Check for and log any invalid booking dates
  useEffect(() => {
    const invalidDates = bookings.filter(booking => {
      try {
        const date = safeParseISO(booking.date, booking.timeSlot);
        return !date;
      } catch (error) {
        return true;
      }
    });
    
    if (invalidDates.length > 0) {
      console.warn(`Found ${invalidDates.length} bookings with invalid dates in WeeklyCalendar`);
      invalidDates.forEach(b => {
        console.warn(`Invalid booking: id=${b.id}, date=${b.date}, timeSlot=${b.timeSlot}`);
      });
    }
  }, [bookings]);

  if (!room) return null;

  // Use Monday as the start of the week
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  
  // For mobile view, we'll show one day at a time
  const mobileDayToShow = currentDayIndex >= 0 ? weekDays[currentDayIndex] : date;

  const getBookingsForDay = (day: Date) => {
    try {
      const dateStr = format(day, 'yyyy-MM-dd');
      const nextDayStr = format(addDays(day, 1), 'yyyy-MM-dd');
      
      return localBookings.filter(booking => {
        try {
          // Make sure we're using just the date part of any ISO string
          const bookingDateClean = booking.date.includes('T') 
            ? booking.date.split('T')[0] 
            : booking.date;
            
          const bookingHour = parseInt(booking.timeSlot.split(':')[0]);
          const isNextDay = bookingHour < 9;
          
          return bookingDateClean === (isNextDay ? nextDayStr : dateStr);
        } catch (error) {
          console.error(`Error processing booking in getBookingsForDay: ${booking.id}`, error);
          return false;
        }
      });
    } catch (error) {
      console.error("Error in getBookingsForDay:", error);
      return [];
    }
  };

  const isSlotAvailable = (day: Date, hour: number) => {
    try {
      const isNextDay = hour < 9;
      const dateStr = format(isNextDay ? addDays(day, 1) : day, 'yyyy-MM-dd');
      
      return !localBookings.some(booking => {
        try {
          // Extract just the date part
          const bookingDateClean = booking.date.includes('T') 
            ? booking.date.split('T')[0] 
            : booking.date;
            
          if (bookingDateClean !== dateStr) return false;
          
          const bookingStart = parseInt(booking.timeSlot.split(':')[0]);
          if (isNaN(bookingStart)) return false;
          
          const bookingEnd = bookingStart + booking.duration;
          return hour >= bookingStart && hour < bookingEnd;
        } catch (error) {
          console.error(`Error checking slot availability for booking: ${booking.id}`, error);
          return false;
        }
      });
    } catch (error) {
      console.error("Error in isSlotAvailable:", error);
      return true; // Default to available if there's an error
    }
  };

  const handleSlotClick = (date: Date, hour: number) => {
    const isNextDay = hour < 9;
    if (isSlotAvailable(date, hour)) {
      setSelectedSlot({ date, hour, isNextDay });
    }
  };

  const handleBookingClick = (booking: Booking) => {
    if (room) {
      setSelectedBooking({ booking, room });
    }
  };

  const handleBookingCreate = (booking: Booking) => {
    setLocalBookings(prev => [...prev, booking]);
    onBookingCreate(booking);
  };

  const handleBookingUpdate = (booking: Booking) => {
    setLocalBookings(prev => 
      prev.map(b => b.id === booking.id ? booking : b)
    );
    onBookingUpdate(booking);
  };

  const handleBookingDelete = (bookingId: number) => {
    setLocalBookings(prev => 
      prev.filter(b => b.id !== bookingId)
    );
    onBookingDelete(bookingId);
  };

  const getStaffColor = (name: string) => {
    const staff = staffMembers.find(s => s.name === name);
    return staff?.color || '';
  };
  
  const navigateToPrevDay = () => {
    if (currentDayIndex > 0) {
      setCurrentDayIndex(currentDayIndex - 1);
    } else {
      setCurrentDayIndex(6);
    }
  };
  
  const navigateToNextDay = () => {
    if (currentDayIndex < 6) {
      setCurrentDayIndex(currentDayIndex + 1);
    } else {
      setCurrentDayIndex(0);
    }
  };

  const renderMobileView = () => {
    const day = mobileDayToShow;
    const dayBookings = getBookingsForDay(day);
    
    return (
      <div className="flex flex-col h-full">
        {/* Day Navigation */}
        <div className="flex items-center justify-between p-2 bg-gray-50 border-b sticky top-0 z-10">
          <Button
            variant="outline"
            size="icon"
            onClick={navigateToPrevDay}
            className="h-9 w-9"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div 
            className={cn(
              "text-center flex-1 font-medium py-2",
              isToday(day) && "text-purple-600"
            )}
            onClick={() => onDayClick?.(day)}
          >
            <div className="text-lg">
              {format(day, 'EEEE')}
            </div>
            <div className="text-sm text-gray-600">
              {format(day, 'MMMM d, yyyy')}
            </div>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={navigateToNextDay}
            className="h-9 w-9"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Time Slots */}
        <div className="flex flex-col flex-1 overflow-y-auto">
          {HOURS.map(hour => {
            const isNextDay = hour < 9;
            const existingBooking = dayBookings.find(booking => {
              try {
                const bookingHour = parseInt(booking.timeSlot.split(':')[0]);
                if (isNaN(bookingHour)) return false;
                
                const bookingEndHour = bookingHour + booking.duration;
                return hour >= bookingHour && hour < bookingEndHour;
              } catch (error) {
                return false;
              }
            });
            
            const timeLabel = `${hour.toString().padStart(2, '0')}:00`;
            
            return (
              <div 
                key={hour}
                className={cn(
                  "border-b relative",
                  isNextDay ? "bg-gray-50/30" : "",
                  existingBooking ? "min-h-[80px]" : "min-h-[50px]"
                )}
              >
                <div className="absolute left-0 top-0 h-full bg-gray-100 w-14 flex items-center justify-center border-r">
                  <div className="text-sm font-medium text-gray-600">
                    {timeLabel}
                    {isNextDay && (
                      <div className="text-[10px] text-gray-500">next day</div>
                    )}
                  </div>
                </div>
                
                <div className="ml-14 p-1 h-full">
                  {existingBooking ? (
                    <div 
                      className={cn(
                        "p-2 h-full rounded cursor-pointer",
                        statusColors[existingBooking.status]
                      )}
                      onClick={() => handleBookingClick(existingBooking)}
                    >
                      <div className="font-semibold text-sm">{existingBooking.clientName}</div>
                      <div className="text-xs flex justify-between">
                        <span>{existingBooking.timeSlot} ({existingBooking.duration}h)</span>
                        <span className={cn(
                          "px-1.5 py-0.5 rounded-md",
                          getStaffColor(existingBooking.bookedBy)
                        )}>
                          {existingBooking.bookedBy}
                        </span>
                      </div>
                    </div>
                  ) : (
                    isSlotAvailable(day, hour) && (
                      <Button
                        variant="ghost"
                        className="w-full h-full rounded-none bg-white/30 hover:bg-gray-50"
                        onClick={() => handleSlotClick(day, hour)}
                      >
                        <span className="sr-only">Book {timeLabel}</span>
                      </Button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderBooking = (booking: Booking, day: Date) => {
    try {
      // Check for malformed date
      const bookingDate = booking.date.includes('T') 
        ? booking.date.split('T')[0] 
        : booking.date;
      
      // Check if this booking is for this day
      const dayStr = format(day, 'yyyy-MM-dd');
      const nextDayStr = format(addDays(day, 1), 'yyyy-MM-dd');
      
      const startHour = parseInt(booking.timeSlot.split(':')[0]);
      if (isNaN(startHour)) {
        console.warn(`Invalid hour in booking: ${booking.id}, timeSlot: ${booking.timeSlot}`);
        return null;
      }
      
      const isNextDay = startHour < 9;
      const targetDate = isNextDay ? nextDayStr : dayStr;
      
      if (bookingDate !== targetDate) return null;
      
      const duration = booking.duration;
      const staffColor = getStaffColor(booking.bookedBy);
      
      // Calculate position based on hour
      const top = ((startHour < 9 ? startHour + 24 : startHour) - 9) * 60;
      const height = duration * 60;
      
      // Create a safe date object for formatting
      const startTime = safeParseISO(booking.date, booking.timeSlot);
      if (!startTime) {
        console.warn(`Invalid startTime for booking: ${booking.id}`);
        return null;
      }
      
      const endTime = addHours(startTime, duration);
      
      return (
        <div 
          key={booking.id}
          className={cn(
            "absolute w-[calc(100%-8px)] mx-1 p-3 rounded cursor-pointer",
            statusColors[booking.status]
          )}
          style={{
            top: `${top}px`,
            height: `${height}px`,
            minHeight: '60px'
          }}
          onClick={() => handleBookingClick(booking)}
        >
          <div className="font-semibold text-base mb-1">{booking.clientName}</div>
          <div className="text-sm mb-1">
            {safeFormatDate(startTime, 'HH:mm')} - {safeFormatDate(endTime, 'HH:mm')}
          </div>
          <div className={cn("text-sm inline-block px-1.5 py-0.5 rounded-md", staffColor)}>
            {booking.bookedBy}
          </div>
        </div>
      );
    } catch (error) {
      console.error(`Error rendering booking: ${booking.id}`, error);
      return null;
    }
  };

  // Mobile view
  if (isCompactView) {
    return (
      <div className="relative bg-white rounded-lg h-full flex flex-col">
        {renderMobileView()}
        
        {selectedSlot && (
          <BookingDialog
            room={room}
            date={selectedSlot.isNextDay ? addDays(selectedSlot.date, 1) : selectedSlot.date}
            hour={selectedSlot.hour}
            onClose={() => setSelectedSlot(null)}
            onSubmit={handleBookingCreate}
          />
        )}

        {selectedBooking && (
          <EditBookingDialog
            booking={selectedBooking.booking}
            room={selectedBooking.room}
            onClose={() => setSelectedBooking(null)}
            onUpdate={handleBookingUpdate}
            onDelete={handleBookingDelete}
          />
        )}
      </div>
    );
  }

  // Desktop view
  return (
    <div className="relative bg-white rounded-lg">
      <div className="flex">
        {/* Time column header (empty cell) */}
        <div className="w-32 flex-shrink-0 bg-gray-100/80 backdrop-blur supports-[backdrop-filter]:bg-gray-100/50 border-r sticky left-0 z-20">
          <div className="h-16 border-b bg-gray-100 flex items-center justify-center font-medium text-gray-600">
            Time / Day
          </div>
        </div>

        {/* Day headers */}
        <div className="flex-1 overflow-x-auto">
          <div className="grid grid-cols-7 h-16 min-w-[1260px] sticky top-0 z-10">
            {weekDays.map((day, index) => (
              <div 
                key={index}
                className={cn(
                  "border-b border-l flex flex-col items-center justify-center bg-gray-100/80 backdrop-blur supports-[backdrop-filter]:bg-gray-100/50",
                  isToday(day) && "bg-blue-50"
                )}
                onClick={() => onDayClick?.(day)}
              >
                <div className="font-bold text-gray-700">
                  {format(day, 'EEE')}
                </div>
                <div className={cn(
                  "text-sm",
                  isToday(day) ? "text-blue-600 font-medium" : "text-gray-500"
                )}>
                  {format(day, 'MMM d')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex">
        <div className="w-32 flex-shrink-0 bg-gray-100/80 backdrop-blur supports-[backdrop-filter]:bg-gray-100/50 border-r sticky left-0 z-10">
          {HOURS.map(hour => {
            const isNextDay = hour < 9;
            return (
              <div
                key={hour}
                className={cn(
                  "h-[60px] border-b text-sm font-medium text-right pr-3 pt-2",
                  isNextDay ? "bg-gray-200/50 text-gray-600" : "text-gray-700",
                  hour % 6 === 0 && "font-semibold"
                )}
              >
                {hour.toString().padStart(2, '0')}:00
                {isNextDay && <div className="text-xs text-gray-500">(next day)</div>}
              </div>
            );
          })}
        </div>

        <div className="flex-1 relative overflow-x-auto">
          <div className="grid grid-cols-7 h-full min-w-[1260px]">
            {weekDays.map(day => (
              <div
                key={day.toString()}
                className={cn(
                  "border-l relative min-w-[180px]",
                  format(day, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd') && 'bg-blue-50/30'
                )}
              >
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-y-0 left-0 right-0 h-[900px] bg-gray-50/30" />
                  <div className="absolute inset-y-0 left-0 right-0 h-[180px] top-[900px] bg-gray-100/30" />
                </div>

                {HOURS.map(hour => {
                  const isNextDay = hour < 9;
                  return (
                    <div
                      key={hour}
                      className={cn(
                        "h-[60px] border-b relative",
                        isNextDay && "bg-gray-50/30",
                        hour % 6 === 0 && "border-b-2 border-gray-200"
                      )}
                    >
                      {isSlotAvailable(day, hour) && (
                        <Button
                          variant="ghost"
                          className="w-full h-full rounded-none bg-white hover:bg-gray-50/75"
                          onClick={() => handleSlotClick(day, hour)}
                        />
                      )}
                    </div>
                  );
                })}

                {getBookingsForDay(day).map(booking => renderBooking(booking, day))}
              </div>
            ))}
          </div>

          {weekDays.some(day => format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')) && (
            <div 
              className="absolute w-full border-t-2 border-blue-500 z-10"
              style={{
                top: `${((new Date().getHours() < 9 ? new Date().getHours() + 24 : new Date().getHours()) - 9) * 60 + new Date().getMinutes()}px`
              }}
            />
          )}
        </div>
      </div>

      {selectedSlot && (
        <BookingDialog
          room={room}
          date={selectedSlot.isNextDay ? addDays(selectedSlot.date, 1) : selectedSlot.date}
          hour={selectedSlot.hour}
          onClose={() => setSelectedSlot(null)}
          onSubmit={handleBookingCreate}
        />
      )}

      {selectedBooking && (
        <EditBookingDialog
          booking={selectedBooking.booking}
          room={selectedBooking.room}
          onClose={() => setSelectedBooking(null)}
          onUpdate={handleBookingUpdate}
          onDelete={handleBookingDelete}
        />
      )}
    </div>
  );
}